import { Anchor, Table, UnstyledButton } from '@mantine/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { countOf, labelOf, type EntityType } from '../../domain/labels.js';
import { rowLabel } from '../../domain/rowLabel.js';
import { configEditRoute, gameEditRoute, gamesRoute, studioEditRoute } from '../../domain/routes.js';
import type { SyncStatus } from '../../domain/status.js';
import { RecordLabel, RowLabelInline } from '../Diff/RecordLabel.js';
import { SectionHeading } from '../SectionHeading/SectionHeading.js';
import { childSelection, rootSelection } from './rowSelection.js';
import type { ChildRow, RootRow, SelectedCell } from './types.js';

/** What the grid and the card list read off any row. */
export type MatrixRow = {
    type: string;
    key: string;
    title: string | null;
    cells: Record<string, { status: SyncStatus } | undefined>;
};

/**
 * Everything type-specific about a matrix row, so `MatrixGrid` and `MatrixCardList` are written
 * once: where its name links, how it is selected, and its one extra fact — a studio's game count,
 * a game's studio — as a grid column and as a card's second line.
 */
export type RowSpec<Row extends MatrixRow> = {
    type: EntityType;
    /** The key column's heading. */
    label: string;
    editHref: (row: Row) => string;
    selectionOf: (row: Row) => SelectedCell;
    /** A row checkbox's aria-label, from the row's display name. */
    selectLabel: (name: string) => string;
    /** The header checkbox's aria-label. */
    selectAllLabel: string;
    extraHeader: ReactNode;
    extraCell: (row: Row, name: string) => ReactNode;
    /** A card's dim line under the label. */
    subtitle: (row: Row) => ReactNode;
};

export const STUDIO_ROWS: RowSpec<RootRow> = {
    type: 'studio',
    label: labelOf('studio').singular,
    editHref: (row) => studioEditRoute(row.key),
    selectionOf: rootSelection,
    selectLabel: (name) => `Select ${name} and everything under it`,
    selectAllLabel: `Select every ${labelOf('studio').singular.toLowerCase()} on this page`,
    extraHeader: (
        <Table.Th style={{ minWidth: 64, textAlign: 'right' }}>
            <SectionHeading variant="column">Games</SectionHeading>
        </Table.Th>
    ),
    extraCell: (row, name) => (
        <Table.Td className="rgs-numeric" style={{ textAlign: 'right' }}>
            <Anchor component={Link} to={gamesRoute(row.key)} fz="sm" aria-label={`Games in ${name}`}>
                {row.childCount}
            </Anchor>
        </Table.Td>
    ),
    subtitle: (row) => (
        <Anchor component={Link} to={gamesRoute(row.key)} fz="xs">
            {countOf('game', row.childCount)}
        </Anchor>
    ),
};

export const GAME_ROWS: RowSpec<ChildRow> = {
    type: 'game',
    label: labelOf('game').singular,
    editHref: (row) => gameEditRoute(row.parentKey, row.shortKey),
    selectionOf: childSelection,
    selectLabel: (name) => `Select ${name}`,
    selectAllLabel: `Select every loaded ${labelOf('game').singular.toLowerCase()}`,
    extraHeader: (
        <Table.Th style={{ minWidth: 160 }}>
            <SectionHeading variant="column">{labelOf('studio').singular}</SectionHeading>
        </Table.Th>
    ),
    extraCell: (row) => (
        <Table.Td>
            <UnstyledButton
                component={Link}
                to={gamesRoute(row.parentKey)}
                aria-label={`Games in ${rowLabel(row.parentTitle, row.parentKey).label}`}
                style={{ display: 'block', cursor: 'pointer' }}
            >
                <RecordLabel layout="stacked" title={row.parentTitle} entityKey={row.parentKey} maw={220} />
            </UnstyledButton>
        </Table.Td>
    ),
    subtitle: (row) => {
        const studio = rowLabel(row.parentTitle, row.parentKey);
        return (
            <Anchor
                component={Link}
                to={gamesRoute(row.parentKey)}
                underline="never"
                c="dimmed"
                fz="xs"
                aria-label={`Games in ${studio.label}`}
                truncate
            >
                <RowLabelInline label={studio} />
            </Anchor>
        );
    },
};

export const CONFIG_ROWS: RowSpec<RootRow> = {
    type: 'config',
    label: labelOf('config').singular,
    editHref: (row) => configEditRoute(row.key),
    selectionOf: rootSelection,
    selectLabel: (name) => `Select ${name}`,
    selectAllLabel: `Select every ${labelOf('config').singular.toLowerCase()} on this page`,
    extraHeader: null,
    extraCell: () => null,
    subtitle: () => null,
};
